import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Play, Loader2, RefreshCw, X, Trash2, Search, Upload,
  SkipBack, SkipForward, BookOpen, Database
} from 'lucide-react';
import { BibleSmartSearch } from '../../SmartSearchPanels';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const BiblePanel = ({ onRefresh }) => {
  const [reference, setReference] = useState('');
  const [topic, setTopic] = useState('');
  const [verses, setVerses] = useState([]);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [translation, setTranslation] = useState('kjv');
  const [yorubaText, setYorubaText] = useState('');
  const [yorubaBookName, setYorubaBookName] = useState('');
  const [showYorubaInput, setShowYorubaInput] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [yorubaStats, setYorubaStats] = useState(null);
  const [fromDatabase, setFromDatabase] = useState(false);

  useEffect(() => { fetchYorubaStats(); }, []);

  const fetchYorubaStats = async () => {
    try {
      const response = await axios.get(`${API}/display/yoruba-bible/stats`);
      if (response.data.success) setYorubaStats(response.data);
    } catch (error) {
      console.error('Error fetching Yoruba stats:', error);
    }
  };

  const quickReferences = [
    { ref: 'John 3:16', label: 'John 3:16' },
    { ref: 'Psalm 23', label: 'Psalm 23' },
    { ref: 'Romans 8:28', label: 'Romans 8:28' },
    { ref: 'Philippians 4:13', label: 'Phil 4:13' },
    { ref: 'Proverbs 3:5-6', label: 'Prov 3:5-6' },
    { ref: 'Isaiah 40:31', label: 'Isaiah 40:31' },
    { ref: 'Matthew 6:33', label: 'Matt 6:33' },
    { ref: 'Jeremiah 29:11', label: 'Jer 29:11' },
  ];

  const handleFetchBible = async (customRef = null) => {
    const searchRef = customRef || reference;
    if (!searchRef) return;
    setLoading(true);
    setCurrentVerseIndex(0);
    setYorubaText('');
    setFromDatabase(false);
    try {
      const response = await axios.get(`${API}/display/bible/${translation}/${encodeURIComponent(searchRef)}`);
      if (response.data.success) {
        if (translation === 'yoruba') {
          setYorubaBookName(response.data.yoruba_book_name || '');
          if (response.data.from_database && response.data.verses.length > 0) {
            setVerses(response.data.verses);
            setFromDatabase(true);
            if (response.data.verses.length === 1) setYorubaText(response.data.verses[0].text || '');
            setShowYorubaInput(false);
          } else {
            setShowYorubaInput(true);
            setReference(searchRef);
            const engResponse = await axios.get(`${API}/display/bible/kjv/${encodeURIComponent(searchRef)}`);
            if (engResponse.data.success && engResponse.data.verses) setVerses(engResponse.data.verses);
          }
        } else {
          setVerses(response.data.verses || []);
          setReference(response.data.reference || searchRef);
          setYorubaBookName(response.data.yoruba_book_name || '');
        }
        setRecentSearches(prev => {
          const filtered = prev.filter(r => r !== searchRef);
          return [searchRef, ...filtered].slice(0, 5);
        });
      }
    } catch (error) {
      console.error('Error fetching Bible:', error);
      try {
        const response = await axios.get(`https://bible-api.com/${encodeURIComponent(searchRef)}?translation=${translation === 'yoruba' ? 'kjv' : translation}`);
        if (response.data && response.data.verses) {
          setVerses(response.data.verses.map(v => ({
            verse: v.verse, text: v.text.trim(), book_name: v.book_name, chapter: v.chapter
          })));
          setReference(response.data.reference);
        }
      } catch (fallbackError) {
        alert('Could not find that passage. Try format: John 3:16 or Psalm 23:1-6');
      }
    }
    setLoading(false);
  };

  const handleShowBible = async () => {
    if (verses.length === 0 && !yorubaText) return;
    const currentVerse = verses[currentVerseIndex];
    const displayText = translation === 'yoruba' && yorubaText ? yorubaText : currentVerse?.text || '';
    try {
      await axios.post(`${API}/display/show/bible`, {
        reference: translation === 'yoruba' && yorubaBookName ? `${yorubaBookName} (${reference})` : reference,
        verse_number: currentVerse?.verse, text: displayText, topic, translation,
        total_verses: verses.length, current_verse_index: currentVerseIndex,
        yoruba_text: translation !== 'yoruba' && yorubaText ? yorubaText : null
      });
    } catch (error) { console.error('Error:', error); }
  };

  const handleVerseNav = async (direction) => {
    const newIndex = currentVerseIndex + direction;
    if (newIndex < 0 || newIndex >= verses.length) return;
    setCurrentVerseIndex(newIndex);
    const v = verses[newIndex];
    try {
      await axios.post(`${API}/display/show/bible`, {
        reference, verse_number: v.verse,
        text: translation === 'yoruba' && yorubaText ? yorubaText : v.text,
        topic, translation, total_verses: verses.length, current_verse_index: newIndex
      });
    } catch (error) { console.error('Error:', error); }
  };

  const handleImportYorubaBible = async () => {
    if (!importData.trim()) { alert('Please paste JSON data to import'); return; }
    setImportLoading(true);
    try {
      let parsedData;
      try { parsedData = JSON.parse(importData); } catch (e) { alert('Invalid JSON format.'); setImportLoading(false); return; }
      let importVerses = Array.isArray(parsedData) ? parsedData : parsedData.verses;
      if (!importVerses) { alert('Invalid format.'); setImportLoading(false); return; }
      const response = await axios.post(`${API}/display/yoruba-bible/import`, { verses: importVerses, format: 'json' });
      if (response.data.success) {
        alert(`Successfully imported ${response.data.imported} verses!${response.data.total_errors > 0 ? `\n${response.data.total_errors} errors.` : ''}`);
        setShowImportModal(false); setImportData(''); fetchYorubaStats();
      }
    } catch (error) {
      alert('Error importing: ' + (error.response?.data?.detail || error.message));
    }
    setImportLoading(false);
  };

  const handleClearYorubaBible = async () => {
    if (!window.confirm('Clear ALL imported Yoruba Bible verses?')) return;
    try {
      const response = await axios.delete(`${API}/display/yoruba-bible/clear`);
      if (response.data.success) { alert(`Cleared ${response.data.deleted} verses`); fetchYorubaStats(); }
    } catch (error) { alert('Error: ' + (error.response?.data?.detail || error.message)); }
  };

  const currentVerse = verses[currentVerseIndex];

  return (
    <div className="space-y-6">
      {/* Smart + Voice Bible Search */}
      <BibleSmartSearch />
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Bible Reading Display</h3>
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
          {['kjv', 'web', 'yoruba'].map(t => (
            <button key={t} onClick={() => setTranslation(t)}
              className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-colors ${
                translation === t ? `bg-white shadow ${t === 'yoruba' ? 'text-purple-600' : 'text-blue-600'}` : 'text-slate-600'
              }`}>
              {t === 'yoruba' ? 'Yoruba' : t.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {quickReferences.map(qr => (
          <button key={qr.ref} onClick={() => handleFetchBible(qr.ref)}
            className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
            {qr.label}
          </button>
        ))}
      </div>

      {/* Yoruba Stats */}
      {translation === 'yoruba' && (
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-purple-800 flex items-center gap-2">
                <Database className="w-4 h-4" /> Yoruba Bible Database
              </h4>
              <p className="text-sm text-purple-600 mt-1">
                {yorubaStats?.total_verses > 0
                  ? `${yorubaStats.total_verses.toLocaleString()} verses from ${yorubaStats.books_count} books`
                  : 'No verses imported yet'}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowImportModal(true)} data-testid="import-yoruba-btn"
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                <Upload className="w-4 h-4" /> Import
              </button>
              {yorubaStats?.total_verses > 0 && (
                <button onClick={handleClearYorubaBible}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200">
                  <Trash2 className="w-4 h-4" /> Clear
                </button>
              )}
            </div>
          </div>
          {yorubaStats?.books?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {yorubaStats.books.slice(0, 10).map(book => (
                <span key={book} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{book}</span>
              ))}
              {yorubaStats.books.length > 10 && (
                <span className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs font-medium">+{yorubaStats.books.length - 10} more</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-800">Import Yoruba Bible Data</h3>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Expected JSON Format:</h4>
                <pre className="text-xs text-blue-700 bg-blue-100 p-2 rounded overflow-x-auto">
{`{"verses": [{"book": "John", "chapter": 3, "verse": 16, "text": "Nitoripe..."}]}`}
                </pre>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Upload JSON File</label>
                <input type="file" accept=".json" data-testid="yoruba-file-input"
                  onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = (ev) => setImportData(ev.target.result); r.readAsText(f); } }}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Or Paste JSON Data</label>
                <textarea value={importData} onChange={(e) => setImportData(e.target.value)}
                  data-testid="yoruba-json-input"
                  className="w-full h-48 px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 font-mono text-sm" />
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowImportModal(false)} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button onClick={handleImportYorubaBible} disabled={importLoading || !importData.trim()} data-testid="confirm-import-btn"
                  className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                  {importLoading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Importing...</> : <><Upload className="w-4 h-4" /> Import Data</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Scripture Reference</label>
            <div className="flex gap-2">
              <input type="text" placeholder="e.g., John 3:16 or Psalm 23:1-6" value={reference}
                onChange={(e) => setReference(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFetchBible()}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                data-testid="bible-reference-input" />
              <button onClick={() => handleFetchBible()} disabled={loading} data-testid="bible-search-btn"
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {recentSearches.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Recent:</p>
              <div className="flex flex-wrap gap-1">
                {recentSearches.map(r => (
                  <button key={r} onClick={() => handleFetchBible(r)}
                    className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs hover:bg-slate-200">{r}</button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Topic/Theme (Optional)</label>
            <input type="text" placeholder="e.g., The Power of Faith" value={topic}
              onChange={(e) => setTopic(e.target.value)} data-testid="bible-topic-input"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500" />
          </div>

          {(translation === 'yoruba' || showYorubaInput) && (
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-800 flex items-center gap-2">
                    Yoruba Translation
                    {fromDatabase && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">From Database</span>}
                  </p>
                  {yorubaBookName && <p className="text-xs text-purple-600">{yorubaBookName}</p>}
                </div>
                {translation !== 'yoruba' && (
                  <button onClick={() => setShowYorubaInput(!showYorubaInput)} className="text-xs text-purple-600 hover:text-purple-800">
                    {showYorubaInput ? 'Hide' : 'Add Yoruba'}
                  </button>
                )}
              </div>
              <textarea placeholder="Enter the Yoruba translation..." value={yorubaText}
                onChange={(e) => setYorubaText(e.target.value)} data-testid="yoruba-text-input"
                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm min-h-[80px]" />
            </div>
          )}

          {verses.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <button onClick={() => handleVerseNav(-1)} disabled={currentVerseIndex === 0}
                className="p-2 bg-white rounded-lg shadow hover:bg-slate-50 disabled:opacity-50">
                <SkipBack className="w-5 h-5" />
              </button>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">Verse {currentVerseIndex + 1} of {verses.length}</p>
                <p className="text-xs text-slate-500">{reference}</p>
              </div>
              <button onClick={() => handleVerseNav(1)} disabled={currentVerseIndex >= verses.length - 1}
                className="p-2 bg-white rounded-lg shadow hover:bg-slate-50 disabled:opacity-50">
                <SkipForward className="w-5 h-5" />
              </button>
            </div>
          )}

          <button onClick={handleShowBible} disabled={verses.length === 0 && !yorubaText} data-testid="display-bible-btn"
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-lg">
            <Play className="w-5 h-5" /> Display {translation === 'yoruba' ? 'Yoruba' : 'Current'} Verse
          </button>
        </div>

        <div className="p-6 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl text-white min-h-72 flex flex-col">
          {topic && <p className="text-amber-400 text-lg font-medium mb-2">{topic}</p>}
          {reference && (
            <h4 className="text-2xl font-bold text-white mb-4">
              {translation === 'yoruba' && yorubaBookName ? yorubaBookName : reference}
              {currentVerse && `:${currentVerse.verse}`}
            </h4>
          )}
          <div className="flex-1 space-y-3">
            {translation === 'yoruba' && yorubaText ? (
              <>
                <p className="text-xl leading-relaxed text-white italic">"{yorubaText}"</p>
                {currentVerse && <p className="text-sm leading-relaxed text-white/50 italic">"{currentVerse.text}"</p>}
              </>
            ) : currentVerse ? (
              <>
                <p className="text-xl leading-relaxed text-white/90 italic">"{currentVerse.text}"</p>
                {yorubaText && <p className="text-sm leading-relaxed text-purple-300/80 italic mt-2">"{yorubaText}"</p>}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-white/30">
                <BookOpen className="w-12 h-12 mb-3" />
                <p>Enter a scripture reference and click search...</p>
              </div>
            )}
          </div>
          {(verses.length > 0 || yorubaText) && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
              <span className={`text-sm ${translation === 'yoruba' ? 'text-purple-400' : 'text-white/40'}`}>
                {translation === 'yoruba' ? 'YORUBA' : translation.toUpperCase()}
              </span>
              {verses.length > 0 && <span className="text-white/40 text-sm">Verse {currentVerseIndex + 1}/{verses.length}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BiblePanel;
